import { IonBreadcrumbs, IonBreadcrumb, IonIcon, IonCard, IonCardTitle, IonCardHeader, IonButton, IonCardContent, IonInput, IonModal, IonTextarea, IonProgressBar, ItemReorderEventDetail } from '@ionic/react';
import AppContainer from '../AppContainer';
import { folder } from 'ionicons/icons';
import Menu from '../components/Menu';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router';
import { useContext, useEffect, useState } from 'react';
import GlobalContext from '../domain/GlobalContext';
import { api } from '../domain/ApiService';
import { Assistant, Folder, Initiative, KnowledgeBaseItem } from '../domain/Types';
import ListView from '../components/ListView';
import KnowledgeBase from '../components/KnowledgeBase';
import { v4 as uuidv4 } from 'uuid';

const Page: React.FC = () => {

  const history = useHistory();

  const { user, initiative, getInitiative } = useContext(GlobalContext);
  const [ isFetched, setIsFetched ] = useState(false);

  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);

  const [ folder, setFolder ] = useState<Folder | null>(null);
  const [ templateFolder, setTemplateFolder ] = useState<Folder | null>(null);
  const [ assistant, setAssistant ] = useState<Assistant | null>(null);

  const location = useLocation();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const initiative_slug = pathSegments[0];
  const folder_slug = pathSegments[1];

  useEffect(() => {

    if (initiative != null && initiative.folders != null && folder == null) {

      setFolder(initiative.folders.find((f: Folder) => f.slug === folder_slug) ?? null);

      if (initiative.template_uid != null) {

        let template = getInitiative(initiative.template_uid);
        if (template != null && template.folders != null) {

          setTemplateFolder(template.folders.find((f: Folder) => f.slug === folder_slug) ?? null);
        }
      }
    }

    setIsFetched(true);

  }, [initiative, folder]);

  const updateInitiative = async () => {

    if (initiative != null) {

      try { 

        const response = await api("/initiative/update", initiative, initiative) as Initiative; 
        window.dispatchEvent(new Event('initiativeUpdated'));
      }
      catch (err) { console.error('Error:', err); }
    }
  }

  const updateFolder = async () => {
    setIsFetched(false);
    await updateInitiative();
    setShowFolderModal(false);
    setIsFetched(true);
  };

  const navigateToParent = () => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    pathSegments.pop();
    const parentPath = `/${pathSegments.join('/')}`;
    history.push(parentPath);
  };  

  const removeFolder = async () => {
    setIsFetched(false);  

    if (initiative != null) {

      let folders = initiative.folders as Folder[];
      initiative.folders = folders.filter(item => item.uid !== folder?.uid);
    }
    await updateInitiative();
    setShowFolderModal(false);
    setIsFetched(true);
    navigateToParent();
  };

  const updateAssistant = async () => {

    if (!folder) return;
    
    setIsFetched(false);

    if (!folder.assistants)
      folder.assistants = [];

    if (folder && folder.assistants && assistant != null && 
        !folder.assistants.find(item => item.uid === assistant.uid)) {

          if (assistant && (!assistant.slug || assistant.slug === "")) {
            assistant.slug = assistant.name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^\w-]/g, '')
                .replace(/^-+|-+$/g, '');
          }

          folder.assistants.push(assistant);
    }
    await updateInitiative();
    setShowAssistantModal(false);
    setAssistant(null);
    setIsFetched(true);
  };

  const removeAssistant = async () => {

    setIsFetched(false);  

    if (initiative == null || initiative.folders == null || !folder) { return; }

    const iFolder = initiative.folders.find(f => f.uid === folder.uid); 

    if (!iFolder) return;

    if (!iFolder.assistants)
      iFolder.assistants = [];
    
    iFolder.assistants = iFolder.assistants.filter(item => item.uid !== assistant?.uid);

    await updateInitiative();
    setShowAssistantModal(false);
    setAssistant(null);
    setIsFetched(true);
  };

  const handleAssistantEdit = (item: Assistant | null) => {
    let editItem = item;
    if (editItem == null) {
      editItem = {  "uid": uuidv4(), "created_by": user?.auth_id, "name": "", "instructions": "", "slug": "", "knowledge_base": [] };
    }
    setAssistant(editItem);
    setShowAssistantModal(true);
  };

  const saveKnowledgeBase = (items: KnowledgeBaseItem[]) => {

    if (folder != null) {

      setIsFetched(false);
      folder.knowledge_base = items;
      updateInitiative();
      setIsFetched(true);
    }
  };

  const handleAssistantClick = (item: Assistant) => {
    history.push('/'+ initiative?.name +'/'+ folder?.slug +'/'+ item.slug, {});
  };

  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    // Existing reorder logic
    event.detail.complete();
  }; 
  
  return (
    <>
      {(isFetched && initiative != null) ? (
      <>
      <IonCard className='filled' style={{ padding: "10px" }}>
        <IonCardHeader>
          <IonCardTitle>Instructions</IonCardTitle>
          <br />              
          <big>{JSON.stringify(folder?.instructions)}</big>
        </IonCardHeader>
        {user && (user.role == 'admin' || user.role === 'superadmin') && (
        <IonCardContent>
          <IonButton className='basic' onClick={() => { setShowFolderModal(true); }}>Modify</IonButton>
        </IonCardContent>
        )}
      </IonCard>

      <KnowledgeBase 
        items={folder?.knowledge_base != null ? folder?.knowledge_base : []} 
        templateItems={templateFolder?.knowledge_base != null ? templateFolder?.knowledge_base : []} 
        folder_uid={folder?.uid} 
        label="Folder knowledge base"
        onSave={saveKnowledgeBase} />

      <IonCard className='filled' style={{ padding: "10px" }}>
        <IonCardHeader>
          <IonCardTitle>Chat rooms</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
            <ListView<Assistant>
              items={folder?.assistants ? folder.assistants : []}
              columns={[
                { attribute: 'name', label: 'Summary', size: "small", primary: true },
                { attribute: 'instructions', label: 'Instructions', size: "large" }
              ]}
              showActions={user?.role == "admin"}
              showCreate={true}
              editMode={user?.role == "admin" ? "admin" : "owner"}
              enableReorder={false}
              defaultLayout="table"
              onEdit={handleAssistantEdit}
              onReorder={handleReorder}
              onClick={handleAssistantClick}
              />
        </IonCardContent>
      </IonCard>

        <IonModal className='form-modal' isOpen={showFolderModal} onDidDismiss={() => setShowFolderModal(false)}>
        <div className='form'>
          <IonInput
            className='form-element'
            label="Name"
            labelPlacement="floating"
            fill="outline"
            value={folder?.name} 
            onIonChange={e => setFolder(prev => {
              if (prev && e.detail.value !== null && e.detail.value !== undefined) {
                  prev.name = e.detail.value;
              }
              return prev;
            })} 
            placeholder="Name"
          />
        
          <IonTextarea 
            className='form-element'
            label="Instruction"
            labelPlacement="floating"
            fill="outline"
            rows={5}            
            value={folder?.instructions}
            onIonChange={e => setFolder(prev => {
              if (prev && e.detail.value !== null && e.detail.value !== undefined) {
                  prev.instructions = e.detail.value;
              }
              return prev;
            })} 
            placeholder="Instruction"
          />

          <IonButton onClick={updateFolder}>Save</IonButton>
          <IonButton className='basic' onClick={() => { setShowFolderModal(false); }}>Cancel</IonButton>
          <IonButton color="danger" onClick={removeFolder}>Remove</IonButton>

          </div>
        </IonModal>
        
        <IonModal className='form-modal' isOpen={showAssistantModal} onDidDismiss={() => setShowAssistantModal(false)}>

          <div className='form'>
            <IonInput
              className='form-element'
              label="Name"
              labelPlacement="floating"
              fill="outline"
              value={assistant?.name} 
              onIonChange={e => setAssistant(prev => {
                if (prev && e.detail.value !== null && e.detail.value !== undefined) {
                    prev.name = e.detail.value;
                }
                return prev;
              })} 
              placeholder="Name"
            />
          
            <IonTextarea 
              className='form-element'
              label="Instruction"
              labelPlacement="floating"
              fill="outline"
              rows={5}            
              value={assistant?.instructions}
              onIonChange={e => setAssistant(prev => {
                if (prev && e.detail.value !== null && e.detail.value !== undefined) {
                    prev.instructions = e.detail.value;
                }
                return prev;
              })} 
              placeholder="Instruction"
            />

            <IonButton onClick={updateAssistant}>Save</IonButton>
            <IonButton className='basic' onClick={() => { setShowAssistantModal(false); }}>Cancel</IonButton>
            <IonButton color="danger" onClick={removeAssistant}>Remove</IonButton>

          </div>
        </IonModal>

      </>
      
      ) : (

        <IonProgressBar type="indeterminate" color="medium"></IonProgressBar>

      )}
    </>
  );
};

const Breadcrumb: React.FC = () => {
    return (
      <IonBreadcrumbs>
        <IonBreadcrumb routerLink="/chat">
          <IonIcon slot="start" icon={folder}></IonIcon>
          Folder
        </IonBreadcrumb>
      </IonBreadcrumbs>
    );
  };
  
const Container: React.FC<{}> = () => { return <AppContainer ContentComponent={Page} BreadcrumbComponent={Breadcrumb} MenuComponent={Menu} />; };
export default Container;